import React from "react";
import { Grid, Modal, Paper, Typography, useMediaQuery } from "@mui/material";
import { Box, Container, Stack, Theme } from "@mui/system";
import { QuestionMark as QuestionMarkIcon, Close as CloseIcon } from "@mui/icons-material";
import SearchField from "./SearchField";
import { FullscreenButton } from "../hooks/toggleFullscreen";
import useTranslations from "containers/hooks/useLanguageHook";
import { useTheme as useAppTheme } from "@mui/material/styles";
import { ResponsiveButton } from "./ResponsiveButton";

type ComponentProps = {
  title: string;
  reference: React.MutableRefObject<HTMLDivElement | undefined>;
  help: {
    title: string;
    description: string;
    image: string;
  };
};

export function VisualizationPageHeader(props: ComponentProps) {
  const breakpoint = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));
  const appTheme = useAppTheme();
  const { t } = useTranslations();

  const [helpModalOpen, setHelpModalOpen] = React.useState(false);

  return (
    <Box>
      <Grid container justifyContent="center" alignItems={"center"} pt={1}>
        <Grid item xs={12} lg={5}>
          <Stack direction="row">
            <Typography
              variant="h4"
              sx={{
                lineHeight: "inherit",
                textAlign: breakpoint ? "left" : "center",
                pb: 0,
                ml: 2,
              }}
            >
              {props.title}
            </Typography>
            {/* show question mark button */}
            <ResponsiveButton
              variant="text"
              text={t.UI.Help}
              onClick={() => setHelpModalOpen(true)}
              tooltip={t.UI.VisualizationHelp}
              icon={<QuestionMarkIcon />}
              iconLocation="start"
              sx={{
                marginLeft: 1,
              }}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Stack
            direction={"row"}
            spacing={2}
            justifyContent={breakpoint ? "right" : "center"}
            pt={0}
            mr={breakpoint ? 2 : "inherit"}
          >
            <SearchField />
            <FullscreenButton elementRef={props.reference} />
          </Stack>
        </Grid>
      </Grid>
      <Modal
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Paper
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "50%",
            minHeight: "50%",
            padding: "1rem",
            border: `2px solid ${appTheme.palette.background.default}`,
            borderRadius: "10px",
            boxShadow: "0px 24px 48px 0 rgba(0,0,0,0.25)",
          }}
        >
          <Box
            style={{
              position: "absolute",
              top: "0.75rem",
              right: "0.75rem",
            }}
            onClick={() => setHelpModalOpen(false)}
          >
            <CloseIcon />
          </Box>
          <Container
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "1rem",
            }}
          >
            <Typography
              id="modal-modal-title"
              variant="h4"
              component="h4"
              style={{
                textAlign: "center",
              }}
            >
              {props.help.title}
            </Typography>
            <img
              src={props.help.image}
              style={{
                marginInline: "auto",
                borderRadius: "4px",
                maxWidth: "100%",
                maxHeight: "300px",
                cursor: "pointer",
              }}
              onClick={() => {
                window.open(props.help.image, "_blank");
              }}
            />
            <Typography
              id="modal-modal-description"
              sx={{ mt: 2 }}
              style={{
                textAlign: "justify",
              }}
            >
              {props.help.description}
            </Typography>
          </Container>
        </Paper>
      </Modal>
    </Box>
  );
}
